import React, { useState } from 'react';
import { Star, MessageSquare, Play, X } from 'lucide-react';

const videoTestimonials = [
  {
    id: 'v1',
    embedUrl: 'https://kinescope.io/embed/7tVxSeNgN49ZNRwuHm82eR',
    name: 'Андрей',
    country: 'ОАЭ',
    flag: '🇦🇪',
    previewImage: 'https://customer-assets.emergentagent.com/job_amazon-course-1/artifacts/mrijgndn_image.png',
  },
  {
    id: 'v2',
    embedUrl: 'https://kinescope.io/embed/0W6tiBtE6ymXmqRjWQGM6r',
    name: 'Дони',
    country: 'Польша',
    flag: '🇵🇱',
    previewImage: 'https://customer-assets.emergentagent.com/job_amazon-course-1/artifacts/cxwizytm_image.png',
  },
  {
    id: 'v3',
    embedUrl: 'https://kinescope.io/embed/rh9rHSLMpETG8vU7jFsPMA',
    name: 'Женя',
    country: 'Италия',
    flag: '🇮🇹',
    previewImage: 'https://customer-assets.emergentagent.com/job_amazon-course-1/artifacts/zm6whnru_image.png',
  },
];

const VideoCard = ({ video, onClick }) => {
  return (
    <div 
      onClick={onClick}
      className="relative rounded-2xl overflow-hidden cursor-pointer group hover:ring-2 hover:ring-amber-500/50 transition-all"
    >
      {/* Real face preview */}
      <div className="aspect-video relative">
        <img 
          src={video.previewImage} 
          alt={video.name}
          className="w-full h-full object-cover"
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Play button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all shadow-2xl shadow-amber-500/40">
            <Play className="w-7 h-7 sm:w-8 sm:h-8 text-black ml-1" />
          </div>
        </div>
        
        {/* Name with flag */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">{video.flag}</span>
            <p className="text-white font-bold text-lg sm:text-xl">{video.name}</p>
          </div>
          <p className="text-zinc-400 text-xs mt-0.5">{video.country}</p>
          <p className="text-amber-400 text-sm mt-1">Смотреть отзыв</p>
        </div>
      </div>
    </div>
  );
};

const VideoModal = ({ video, onClose }) => {
  // Блокируем скролл когда модалка открыта
  React.useEffect(() => {
    if (video) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [video]);

  if (!video) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 z-10 w-14 h-14 rounded-full bg-zinc-800/90 border border-zinc-700 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors"
      >
        <X className="w-7 h-7" />
      </button>
      <div 
        className="w-full max-w-5xl aspect-video"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={`${video.embedUrl}?autoplay=1`}
          className="w-full h-full rounded-2xl"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
};

const TestimonialsSection = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <>
      {/* Модалка вне секции чтобы не обрезалась overflow:hidden */}
      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
      
      <section className="relative py-20 sm:py-24 lg:py-32 overflow-hidden bg-[#050506]" id="testimonials">
        
        <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-amber-500/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-orange-500/[0.02] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/15 to-orange-500/15 border border-amber-500/25 mb-6">
            <MessageSquare className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 font-semibold text-sm sm:text-base">Отзывы учеников</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black mb-4">
            <span className="bg-gradient-to-b from-white via-white to-zinc-500 bg-clip-text text-transparent">Что говорят </span>
            <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-amber-400 bg-clip-text text-transparent">ученики</span>
          </h2>
          
          <div className="flex items-center justify-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 sm:w-6 sm:h-6 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <p className="text-zinc-400 text-base sm:text-lg">50+ учеников уже зарабатывают на Amazon</p>
        </div>

        {/* Video Testimonials - большие карточки */}
        <div className="grid md:grid-cols-3 gap-6">
          {videoTestimonials.map((video) => (
            <VideoCard key={video.id} video={video} onClick={() => setActiveVideo(video)} />
          ))}
        </div>
      </div>
    </section>
    </>
  );
};

export default TestimonialsSection;
